<template>
  <v-col class="pa-0">
    <!-- Breadcrumbs -->
    <v-row class="ma-0 align-center">
      <v-col class="col-auto py-0">
        <bread-crumbs />
      </v-col>
      <v-spacer />
    </v-row>

    <!-- Compact Header with Inline Tabs -->
    <div class="adx-and-ob-view-header-compact mb-3">
      <v-toolbar
        flat
        class="header-toolbar px-0 py-0"
        height="56"
      >
        <!-- Title Icon -->
        <div class="header-icon-wrapper mr-3 ml-3">
          <font-awesome-icon
            :icon="['fas', 'handshake']"
            size="lg"
            class="primary--text"
          />
        </div>

        <!-- Inline Tabs -->
        <v-tabs
          v-model="activeTab"
          color="primary"
          slider-color="primary"
          show-arrows
          class="adx-and-ob-view-tabs-inline flex-grow-1"
          height="56"
        >
          <v-tab
            v-for="tab in tabsWithCounts"
            :key="tab.id"
            class="tab-compact"
          >
            <font-awesome-icon
              v-if="tab.icon"
              :icon="tab.icon"
              class="mr-2"
              size="sm"
            />
            {{ tab.label }}
            <v-chip
              v-if="tab.count !== undefined"
              x-small
              outlined
              :class="`ml-2 tab-count-chip ${tab.count === 0 ? 'gray--text' : `${tab.chipColor || ''}--text`}`"
            >
              {{ tab.count }}
            </v-chip>
          </v-tab>
        </v-tabs>
      </v-toolbar>

      <v-divider />

      <!-- Controls Bar -->
      <div class="controls-bar">
        <v-row class="align-center ma-0 pa-3 pl-2">
          <!-- Search Field -->
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="py-1"
          >
            <search-field
              v-model="searchText"
              :placeholder="showDashboard ? 'Search across all networks...' : 'Search by yield partner or demand channel...'"
            />
          </v-col>

          <!-- Report View Toolbar Buttons -->
          <template v-if="!showDashboard">
            <!-- Data Tools (Group, Sort, Columns) with integrated Filter -->
            <v-col
              v-if="showFilterBar"
              cols="auto"
              class="py-1"
            >
              <tools-toolbar
                :items="items"
                :current-required-columns="currentRequiredColumns"
                :report-key="reportKey"
                :table-columns="tableColumns"
                @on-grouping-change="onGroupingChange"
                @on-sorting-change="onSortingChange"
              >
                <template v-slot:filter-controls>
                  <filter-panel
                    v-model="localFilterData"
                    :fields="yieldPartnerFilterFields"
                    view-name="adx-and-ob"
                    title="Filter AdX & OB"
                    :show-saved-filters="false"
                  />
                </template>
              </tools-toolbar>
            </v-col>

            <v-spacer />

            <!-- Export Button -->
            <v-col
              v-if="showExportBar"
              cols="auto"
              class="py-1"
            >
              <v-menu
                offset-y
                nudge-bottom="6"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="primary"
                    depressed
                    v-bind="attrs"
                    v-on="on"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'circle-down']"
                      size="sm"
                      :class="{ 'mr-1': $vuetify.breakpoint.mdAndUp }"
                    />
                    <span class="d-none d-md-inline">Export</span>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item
                    v-for="exportItem in exportOptions"
                    :key="exportItem.key"
                    :disabled="!exportItem.active"
                    @click="exportItem.callback"
                  >
                    <v-list-item-title v-if="exportItem.key !== 'xlsx'">
                      {{ exportItem.text }}
                    </v-list-item-title>
                    <v-list-item-title v-else>
                      <export-xlsx
                        :item="exportItem"
                        :show-export-bar="showExportBar"
                      />
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </template>

          <!-- Dashboard View Toolbar Buttons -->
          <template v-if="showDashboard">
            <v-col
              cols="auto"
              class="py-1"
            >
              <tools-toolbar
                self-managed
                :items="assignedNetworksForCards"
                :sort-options="dashboardSortOptions"
                :group-options="dashboardGroupOptions"
                :table-columns="dashboardColumns"
                :current-required-columns="dashboardRequiredColumns"
                view-context="Card Properties"
                @on-sorting-change="onDashboardSortingChange"
                @on-grouping-change="onDashboardGroupingChange"
                @on-columns-change="onDashboardColumnsChange"
              />
            </v-col>
          </template>
        </v-row>
      </div>
    </div>

    <!-- Jump to Group -->
    <jump-to-group
      :groups="showDashboard ? dashboardFilteredGroups : filteredGroups"
      class="ma-0 mb-1 ml-3"
      @scroll-to-group="scrollToGroup"
    />

    <!-- Main Content: Dashboard -->
    <v-row
      v-if="showDashboard"
      class="ma-0"
    >
      <v-col>
        <v-card
          v-if="loadingDashboardNetworks"
          class="pa-8 text-center mb-4"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          />
          <div class="mt-4 subtitle-1">
            Loading networks...
          </div>
        </v-card>

        <report-list
          :search-text="searchText"
          :is-external-user="isUserExternal"
          :tab-configs="tabs"
          :assigned-networks="sortedNetworksForCards"
          :current-tab-id="currentTab.id"
          :visible-columns="dashboardVisibleColumns"
          report-path="/reports/adx-and-ob"
        />
      </v-col>
    </v-row>

    <!-- Main Content: Report -->
    <v-row
      v-else
      class="ma-0"
    >
      <v-col>
        <!-- Loading state while fetching -->
        <v-card
          v-if="loadingReportData && items.length === 0"
          class="pa-8 text-center"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          />
          <div class="mt-4 subtitle-1">
            Loading AdX &amp; OB data...
          </div>
        </v-card>

        <data-table
          v-else
          ref="table"
          :data-groups="filteredGroups"
          :hide-toolbar-buttons="true"
        >
          <template v-slot:empty-state>
            <div>Selected publisher has no AdX &amp; OB data for the selected date range.</div>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import api from '@/store/api'
import groupUtil from '@/utils/group'
import logger from '@/plugins/logger'
import columns from '@/delivery/store/utils/columns'
import columnUtils from '@/tools/store/utils/columns'
import PATHS from '@/router/paths'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutsGetters } = createNamespacedHelpers('layout')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

const REPORT_KEY = PATHS.REPORTS.YIELD_PARTNER

export default {
  name: 'AdxAndOb',

  components: {
    BreadCrumbs: () => import('@/layout/components/BreadCrumbs'),
    DataTable: () => import('@/tools/components/table/DataTable'),
    exportXlsx: () => import('@/tools/components/export/ExportXlsx'),
    filterPanel: () => import('@/tools/components/filter/FilterPanel.vue'),
    JumpToGroup: () => import('@/tools/components/filter/JumpToGroup.vue'),
    ReportList: () => import('@/dashboard/components/ReportList'),
    SearchField: () => import('@/tools/components/filter/SearchField.vue'),
    toolsToolbar: () => import('@/tools/components/filter/ToolsToolbar.vue'),
  },

  beforeRouteLeave(to, from, next) {
    if (from.query.tab) {
      const query = { ...from.query }
      delete query.tab
      this.$router.replace({ ...from, query }).catch((err) => {
        logger.console.error(['[AdxAndOb] Error removing tab query parameter:', err])
      })
    }
    next()
  },

  data() {
    return {
      searchText: '',
      activeTab: 0,

      // Tab configurations
      tabs: [
        {
          id: 'all',
          label: 'All',
          icon: ['fas', 'chart-bar'],
          chipColor: 'primary',
        },
        {
          id: 'google',
          label: 'Google',
          icon: ['fas', 'circle-dot'],
          chipColor: 'info',
          tooltip: 'Rows served by Google (AdX)',
        },
        {
          id: 'third-party',
          label: 'Third Party',
          icon: ['fas', 'handshake'],
          chipColor: 'secondary',
          tooltip: 'Rows served by third-party Open Bidding partners',
        },
      ],

      dashboardTabs: [
        {
          id: 'all-networks',
          label: 'Networks',
          icon: ['fas', 'list'],
          chipColor: 'primary',
        },
      ],

      // Dashboard data
      allNetworksData: [],
      loadingDashboardNetworks: false,

      // Report mode data (component-level)
      reportData: [],
      loadingReportData: false,
      isRefreshing: false,

      // Component-level grouping state
      localGroupByProperties: [],

      // Component-level sorting state
      localSortingByKeys: [],
      localSortingByDirections: [],

      // Component-level filter state
      localFilterData: {},
      localFilterKeys: [],

      // Dashboard-level controls (self-managed)
      dashboardSortConfig: [],
      dashboardGroupByProperties: [],
      dashboardColumns: [
        { text: 'Billing Code', value: 'billingCode', visible: true },
        { text: 'Network', value: 'networkCode', visible: true },
      ],
    }
  },

  computed: {
    ...mapAuthGetters([
      'isInternalUser',
      'currentUser',
    ]),
    ...mapLayoutsGetters([
      'showExportBar',
      'showFilterBar',
    ]),
    ...mapToolsGetters([
      'currentColumns',
      'currentGroupByProperties',
      'currentRequiredColumns',
      'reportKey',
      'showSummaries',
      'sortByDirections',
      'sortByKeys',
      'tableColumns',
    ]),
    ...mapToolsState({
      reportName: (state) => state.reportName,
      searchResults: (state) => state.searchResults,
      selectedSearch: (state) => state.selectedSearch,
      currentReportParams: (state) => state.currentReportParams,
    }),

    isUserExternal() {
      return !this.isInternalUser
    },

    /**
     * Dashboard mode: no specific network selected (landing page).
     * Report mode: billingCode query param or path reportId present.
     */
    showDashboard() {
      const hasBillingCodeParam = !!this.$route.query.billingCode
      const hasPathParams = !!this.$route.params.reportId
      return !hasBillingCodeParam && !hasPathParams
    },

    currentTab() {
      return this.tabs[this.activeTab] || this.tabs[0]
    },

    activeTabs() {
      return this.showDashboard ? this.dashboardTabs : this.tabs
    },

    /** All report data from component state */
    items() {
      return this.reportData || []
    },

    // Tab-filtered items
    tabItems() {
      const all = this.items
      const tabId = this.currentTab?.id

      if (!tabId || tabId === 'all') return all

      if (tabId === 'google') {
        return all.filter((item) => item.yieldPartner === null || item.yieldPartner === undefined)
      }

      if (tabId === 'third-party') {
        return all.filter((item) => item.yieldPartner !== null && item.yieldPartner !== undefined)
      }

      return all
    },

    // Apply user filters and search
    filteredItems() {
      let items = this.tabItems

      // Apply FilterPanel filters
      if (this.localFilterKeys.length > 0) {
        items = items.filter((item) => this.localFilterKeys.every((key) => {
          const filterValue = this.localFilterData[key]
          const itemValue = item[key]

          if (Array.isArray(filterValue)) {
            if (filterValue.length === 0) return true
            if (Array.isArray(itemValue)) {
              return filterValue.some((fv) => itemValue.includes(fv))
            }
            return filterValue.some((fv) => {
              if (typeof fv === 'string' && typeof itemValue === 'string') {
                return fv.toLowerCase() === itemValue.toLowerCase()
              }
              return fv === itemValue
            })
          }

          if (typeof filterValue === 'string' && typeof itemValue === 'string') {
            return filterValue.toLowerCase() === itemValue.toLowerCase()
          }
          return filterValue === itemValue
        }))
      }

      // Apply search text
      if (!this.searchText || !this.searchText.trim()) return items

      const searchLower = this.searchText.toLowerCase().trim()
      return items.filter((item) => {
        const partner = (item.yieldPartner === null || item.yieldPartner === undefined ? 'Google' : String(item.yieldPartner)).toLowerCase()
        const channel = (item.demandChannelName || '').toLowerCase()
        const dcid = String(item.demandChannelCode || '').toLowerCase()
        return partner.includes(searchLower)
          || channel.includes(searchLower)
          || dcid.includes(searchLower)
      })
    },

    // Sorted items
    sortedItems() {
      const items = this.filteredItems
      if (!items || items.length === 0) return items

      const sortKeys = this.localSortingByKeys
      const sortDirections = this.localSortingByDirections

      if (!sortKeys || sortKeys.length === 0) return items

      const sorted = [...items]
      sorted.sort((a, b) => {
        let result = 0
        let i = 0
        while (result === 0 && i < sortKeys.length) {
          const key = sortKeys[i]
          const isDesc = sortDirections[i] === true
          let va = a[key]
          let vb = b[key]

          if (va == null && vb != null) result = 1
          else if (va != null && vb == null) result = -1
          else if (va != null && vb != null) {
            if (typeof va === 'string') va = va.toLowerCase()
            if (typeof vb === 'string') vb = vb.toLowerCase()
            if (va < vb) result = -1
            else if (va > vb) result = 1
          }
          if (result !== 0 && isDesc) result = -result
          i += 1
        }
        return result
      })
      return sorted
    },

    // Groups for DataTable
    filteredGroups() {
      if (this.showDashboard) return []

      if (!this.sortedItems || this.sortedItems.length === 0) {
        return [{
          groupName: null,
          labels: ['Yield Partners'],
          items: [],
          isPartOfGroup: false,
          isEmpty: true,
        }]
      }

      if (this.localGroupByProperties && this.localGroupByProperties.length > 0) {
        const groups = groupUtil.byProperty(this.sortedItems, this.localGroupByProperties)
        return groups.map((grp) => ({
          ...grp,
          key: grp.groupName,
          name: grp.labels.join(' :: '),
          isEmpty: grp.items.length === 0,
        }))
      }

      return [{
        key: 'all',
        name: 'All Items',
        groupName: null,
        labels: ['Yield Partners'],
        items: this.sortedItems || [],
        isPartOfGroup: false,
        isEmpty: false,
      }]
    },

    // Tab counts
    tabsWithCounts() {
      if (this.showDashboard) {
        return this.dashboardTabs.map((tab) => ({
          ...tab,
          count: this.allNetworksData.length,
        }))
      }

      return this.tabs.map((tab) => {
        if (tab.id === 'all') return { ...tab, count: this.items.length }
        if (tab.id === 'google') {
          return {
            ...tab,
            count: this.items.filter((i) => i.yieldPartner === null || i.yieldPartner === undefined).length,
          }
        }
        if (tab.id === 'third-party') {
          return {
            ...tab,
            count: this.items.filter((i) => i.yieldPartner !== null && i.yieldPartner !== undefined).length,
          }
        }
        return tab
      })
    },

    // Network cards for dashboard
    assignedNetworksForCards() {
      if (!this.showDashboard) return []

      return this.allNetworksData.map((network) => ({
        text: network.publisherName,
        billingCode: network.billingCode,
        networkCode: network.networkCode || network.publisherId,
        publisherName: network.publisherName,
        publisherId: network.publisherId,
        teamId: -1,
        teamCode: -1,
        isSearchOption: true,
        route: 'reports',
        reportKey: REPORT_KEY,
        reportId: network.billingCode,
        subId: '-1',
        reportName: REPORT_KEY,
        reportPath: REPORT_KEY,
      }))
    },

    // Sorted network cards for dashboard
    sortedNetworksForCards() {
      const cards = [...(this.assignedNetworksForCards || [])]
      if (cards.length === 0) return cards

      if (this.dashboardSortConfig.length > 0) {
        cards.sort((a, b) => this.dashboardSortConfig.reduce((result, sort) => {
          if (result !== 0) return result
          const dir = sort.direction === 'desc' ? -1 : 1
          const va = a[sort.column]
          const vb = b[sort.column]
          if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
          return String(va || '').localeCompare(String(vb || '')) * dir
        }, 0))
      }

      // Apply search on dashboard
      if (this.searchText && this.searchText.trim()) {
        const q = this.searchText.toLowerCase().trim()
        return cards.filter((c) => (c.text || '').toLowerCase().includes(q)
          || (c.billingCode || '').toLowerCase().includes(q))
      }

      return cards
    },

    // Dashboard groups for JumpToGroup
    dashboardFilteredGroups() {
      if (!this.showDashboard || this.dashboardGroupByProperties.length === 0) return []
      const cards = this.assignedNetworksForCards || []
      if (cards.length === 0) return []
      const groupKey = this.dashboardGroupByProperties[0]
      const groupMap = {}
      cards.forEach((card) => {
        const value = card[groupKey] || 'Unknown'
        if (!groupMap[value]) groupMap[value] = { groupName: value, labels: [value], items: [] }
        groupMap[value].items.push(card)
      })
      return Object.values(groupMap).sort((a, b) => a.groupName.localeCompare(b.groupName))
    },

    // FilterPanel fields for adx-and-ob
    yieldPartnerFilterFields() {
      const partners = [...new Set(
        this.items.map((i) => (i.yieldPartner === null || i.yieldPartner === undefined ? 'Google' : String(i.yieldPartner))).filter(Boolean),
      )].sort()
      const channels = [...new Set(this.items.map((i) => i.demandChannelName).filter(Boolean))].sort()

      return [
        {
          key: 'yieldPartner',
          label: 'Yield Partner',
          type: 'multiselect',
          icon: ['fas', 'handshake'],
          items: partners,
          multiple: true,
        },
        {
          key: 'demandChannelName',
          label: 'Demand Channel',
          type: 'multiselect',
          icon: ['fas', 'tower-broadcast'],
          items: channels,
          multiple: true,
        },
      ]
    },

    exportOptions() {
      return [
        {
          active: this.showExportBar,
          key: 'csv',
          text: 'CSV',
          callback: this.exportAsCsv,
        },
        {
          active: this.showExportBar,
          key: 'xlsx',
          text: 'XLSX',
          callback: this.exportAsXlsx,
        },
      ]
    },

    dashboardSortOptions() {
      return [
        { text: 'Name', value: 'text' },
        { text: 'Billing Code', value: 'billingCode' },
        { text: 'Network Code', value: 'networkCode' },
      ]
    },

    dashboardGroupOptions() {
      return [
        { text: 'Billing Code', value: 'billingCode' },
        { text: 'Network Code', value: 'networkCode' },
      ]
    },

    dashboardRequiredColumns() {
      return []
    },

    dashboardVisibleColumns() {
      return this.dashboardColumns
        .filter((col) => col.visible !== false)
        .map((col) => col.value)
    },

    /** Date range from tools store params, or default */
    currentDateRange() {
      return this.currentReportParams?.dateRange || null
    },
  },

  watch: {
    activeTab(newTab, oldTab) {
      if (newTab !== oldTab) {
        const tabId = this.tabs[newTab]?.id
        this.updateUrlWithTab(tabId)
      }
    },

    '$route.query.tab': {
      immediate: true,
      handler(newTab) {
        if (newTab) {
          const tabIndex = this.tabs.findIndex((tab) => tab.id === newTab)
          if (tabIndex !== -1 && tabIndex !== this.activeTab) {
            this.activeTab = tabIndex
          }
        }
      },
    },

    '$route.query.billingCode': {
      async handler(newBillingCode, oldBillingCode) {
        if (!newBillingCode) return
        if (oldBillingCode === newBillingCode) return

        logger.console.info(['[AdxAndOb] billingCode query param changed', { newBillingCode }])

        this.activeTab = 0
        await this.$nextTick()

        const publisherId = await this.resolvePublisherId(newBillingCode)
        this.setSelectedSearch(newBillingCode, publisherId, this.$route.query.teamId || '-1')

        this.loadingReportData = true
        this.reportData = []
        try {
          this.reportData = await this.fetchYieldData(publisherId)
          logger.console.info([`[AdxAndOb] Fetched ${this.reportData.length} items via billingCode watcher`])
        } catch (err) {
          logger.console.error(['[AdxAndOb] Error fetching data via billingCode watcher:', err])
        } finally {
          this.loadingReportData = false
        }
      },
    },

    showDashboard: {
      async handler(isDashboard, wasDashboard) {
        if (isDashboard === wasDashboard) return
        if (isDashboard) {
          this.reportData = []
          await this.fetchDashboardNetworks()
        }
      },
    },

    selectedSearch: {
      immediate: true,
      async handler(newSelection, oldSelection) {
        if (this.showDashboard) return
        if (!newSelection) return
        if (this.$route.query.billingCode) return

        if (oldSelection
            && newSelection.reportId === oldSelection.reportId
            && newSelection.subId === oldSelection.subId) {
          return
        }

        await this.fetchReportData()
      },
    },

    localFilterData: {
      deep: true,
      handler(newData) {
        this.localFilterKeys = Object.keys(newData || {})
      },
    },

    currentGroupByProperties: {
      immediate: true,
      handler(newGrouping) {
        if (newGrouping && Array.isArray(newGrouping)) {
          this.localGroupByProperties = [...newGrouping]
        }
      },
    },

    sortByKeys: {
      immediate: true,
      handler(newKeys) {
        if (newKeys && Array.isArray(newKeys)) this.localSortingByKeys = [...newKeys]
      },
    },

    sortByDirections: {
      immediate: true,
      handler(newDirs) {
        if (newDirs && Array.isArray(newDirs)) this.localSortingByDirections = [...newDirs]
      },
    },
  },

  async created() {
    this.$store.commit('tools/reportName', REPORT_KEY)

    // Initialize columns
    const existingColumns = this.$store.state.tools.columns[REPORT_KEY]
    const reportColumns = columns[REPORT_KEY]

    if (reportColumns && !columnUtils.hasCompatibleColumnSchema(existingColumns, reportColumns)) {
      const storedPrefs = columnUtils.loadColumnPreferences(REPORT_KEY)
      const mergedColumns = columnUtils.applyColumnPreferences(reportColumns, storedPrefs)
      this.$store.commit('tools/columns', { [REPORT_KEY]: mergedColumns })
    }

    // Initialize default sorting
    const validKeys = (reportColumns || [])
      .filter((col) => col.sortable)
      .map((col) => col.key)
    const currentSortKeys = this.$store.state.tools.sortingByKeys || []
    const hasValidSort = currentSortKeys.length > 0 && currentSortKeys.every((k) => validKeys.includes(k))
    if (!hasValidSort) {
      this.$store.commit('tools/sortingByKeys', ['date', 'yieldPartner'])
      this.$store.commit('tools/sortingByDirections', ['asc', 'asc'])
    }

    // Initialize tab from URL
    const tabParam = this.$route.query.tab
    if (tabParam) {
      const tabIndex = this.tabs.findIndex((tab) => tab.id === tabParam)
      if (tabIndex !== -1) this.activeTab = tabIndex
    }

    // Handle navigation with query params (billingCode)
    const { billingCode: queryBillingCode, teamId: queryTeamId } = this.$route.query
    if (queryBillingCode) {
      logger.console.info(['[AdxAndOb] created() - query params', { billingCode: queryBillingCode }])
      const publisherId = await this.resolvePublisherId(queryBillingCode)
      this.setSelectedSearch(queryBillingCode, publisherId, queryTeamId || '-1')

      this.loadingReportData = true
      this.reportData = []
      try {
        this.reportData = await this.fetchYieldData(publisherId)
        logger.console.info([`[AdxAndOb] created() - Fetched ${this.reportData.length} items (query params)`])
      } catch (err) {
        logger.console.error(['[AdxAndOb] created() - Error fetching data:', err])
      } finally {
        this.loadingReportData = false
      }
      return
    }

    // Handle navigation with path params
    const { reportId: pathBillingCode, subId: pathTeamId } = this.$route.params
    if (pathBillingCode) {
      logger.console.info(['[AdxAndOb] created() - path params', { billingCode: pathBillingCode }])
      const publisherId = await this.resolvePublisherId(pathBillingCode)
      this.setSelectedSearch(pathBillingCode, publisherId, pathTeamId || '-1')

      this.loadingReportData = true
      this.reportData = []
      try {
        this.reportData = await this.fetchYieldData(publisherId)
        logger.console.info([`[AdxAndOb] created() - Fetched ${this.reportData.length} items (path params)`])
      } catch (err) {
        logger.console.error(['[AdxAndOb] created() - Error fetching data:', err])
      } finally {
        this.loadingReportData = false
      }
      return
    }

    // Dashboard mode: load all networks
    if (this.showDashboard) {
      await this.fetchDashboardNetworks()
    }
  },

  methods: {
    /**
     * Resolve publisherId from a billingCode.
     * Checks selectedSearch, allNetworksData, and API as fallbacks.
     */
    async resolvePublisherId(billingCode) {
      if (this.selectedSearch?.publisherId && this.selectedSearch?.billingCode === billingCode) {
        return this.selectedSearch.publisherId
      }

      const cached = this.allNetworksData.find((n) => n.billingCode === billingCode)
      if (cached?.publisherId) return cached.publisherId

      try {
        const response = await api.getPublishersByBillingCodes(billingCode)
        const publisher = response?.content?.[0]
        if (publisher) {
          logger.console.info(['[AdxAndOb] Resolved publisherId from API:', publisher.publisherId])
          return publisher.publisherId
        }
      } catch (err) {
        logger.console.error(['[AdxAndOb] Error looking up publisherId:', err])
      }

      logger.console.warn(['[AdxAndOb] No publisherId found, falling back to billingCode:', billingCode])
      return billingCode
    },

    /**
     * Set selectedSearch in the tools store so breadcrumbs and SearchBar
     * display the correct selection.
     */
    setSelectedSearch(billingCode, publisherId, teamId = '-1') {
      const existing = this.selectedSearch
      const publisherName = existing?.publisherName || existing?.text || billingCode

      this.$store.commit('tools/selectedSearch', {
        isSearchOption: true,
        route: 'reports',
        reportKey: REPORT_KEY,
        reportId: billingCode,
        billingCode,
        subId: teamId,
        teamId,
        reportName: REPORT_KEY,
        reportPath: REPORT_KEY,
        publisherId,
        publisherName,
        text: publisherName,
      })
    },

    /**
     * Fetch yield partner report data for the current selectedSearch.
     */
    async fetchReportData() {
      const { selectedSearch } = this
      const networkId = selectedSearch?.publisherId || selectedSearch?.billingCode || selectedSearch?.reportId

      if (!selectedSearch || !networkId) {
        logger.console.warn(['[AdxAndOb] fetchReportData called without valid selectedSearch'])
        return
      }

      this.loadingReportData = true
      this.reportData = []

      try {
        this.reportData = await this.fetchYieldData(networkId)
        logger.console.info([`[AdxAndOb] fetchReportData - Fetched ${this.reportData.length} items`])
      } catch (err) {
        logger.console.error(['[AdxAndOb] fetchReportData - Error:', err])
      } finally {
        this.loadingReportData = false
      }
    },

    /**
     * Fetch yield partner data from the API.
     * @param {string|number} publisherId
     * @returns {Promise<Array>}
     */
    async fetchYieldData(publisherId) {
      if (!publisherId) {
        logger.console.error(['[AdxAndOb] fetchYieldData called without publisherId'])
        return []
      }

      try {
        const dateRange = this.currentDateRange
        const response = await api.getYieldPartner({ publisherId, dateRange })

        const { content, error } = response || {}

        if (error) {
          logger.console.error([`[AdxAndOb] API error for publisher ${publisherId}:`, error])
          return []
        }

        if (!Array.isArray(content)) {
          logger.console.warn([`[AdxAndOb] Publisher ${publisherId} returned non-array content:`, content])
          return []
        }

        return content
      } catch (err) {
        logger.console.error([`[AdxAndOb] Exception fetching data for publisher ${publisherId}:`, err])
        return []
      }
    },

    /**
     * Fetch all publishers with yield partner enabled for the dashboard.
     */
    async fetchDashboardNetworks() {
      this.loadingDashboardNetworks = true
      try {
        const response = await api.getPublishersWithYieldPartner()
        const publishers = response?.content || []

        this.allNetworksData = publishers.map((pub) => ({
          publisherId: pub.publisherId,
          networkCode: pub.networkCode || pub.publisherId,
          publisherName: pub.name,
          billingCode: pub.billingCode,
        }))

        logger.console.info([`[AdxAndOb] Loaded ${this.allNetworksData.length} networks for dashboard`])
      } catch (err) {
        logger.console.error(['[AdxAndOb] Error fetching dashboard networks:', err])
        this.allNetworksData = []
      } finally {
        this.loadingDashboardNetworks = false
      }
    },

    async refreshData() {
      this.isRefreshing = true
      try {
        if (this.showDashboard) {
          await this.fetchDashboardNetworks()
        } else {
          await this.fetchReportData()
        }
      } finally {
        this.isRefreshing = false
      }
    },

    onGroupingChange(groupByProperties) {
      this.localGroupByProperties = groupByProperties || []
    },

    onSortingChange({ keys, directions }) {
      this.localSortingByKeys = keys || []
      this.localSortingByDirections = directions || []
    },

    onDashboardSortingChange(sortConfig) {
      this.dashboardSortConfig = sortConfig || []
    },

    onDashboardGroupingChange(groupByProperties) {
      this.dashboardGroupByProperties = groupByProperties || []
    },

    onDashboardColumnsChange(newColumns) {
      this.dashboardColumns = newColumns || []
    },

    scrollToGroup(groupName) {
      const element = document.getElementById(`group-${groupName}`)
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },

    updateUrlWithTab(tabId) {
      const currentQuery = this.$route.query || {}
      if (tabId === 'all') {
        const { tab, ...restQuery } = currentQuery
        if (tab) {
          this.$router.replace({ ...this.$route, query: restQuery }).catch(() => {})
        }
      } else {
        this.$router.replace({
          ...this.$route,
          query: { ...currentQuery, tab: tabId },
        }).catch(() => {})
      }
    },

    exportAsCsv() {
      if (this.$refs.table?.exportAsCsv) this.$refs.table.exportAsCsv()
    },

    exportAsXlsx() {
      if (this.$refs.table?.exportAsXlsx) this.$refs.table.exportAsXlsx()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.adx-and-ob-view-header-compact {
  background-color: var(--v-surface-base, #ffffff);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  .header-toolbar {
    border-bottom: none;
  }

  .header-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
  }

  .controls-bar {
    background-color: var(--v-surface-base, #ffffff);
  }
}

.tab-compact {
  min-width: 80px;
  font-size: 0.8125rem;
  text-transform: none;
  letter-spacing: 0;
}

.tab-count-chip {
  font-size: 0.6875rem;
}
</style>
